/**
 * Firm info panel (ADR-0077, Phase 2 redesign; ADR-0086, document upload +
 * delete) — the LF-admin record card for a firm. Opens slide-over from a
 * Firms-roster row.
 *
 * The panel answers ONE question: do we have everything on file for this firm?
 * Sections lean on present-or-absent affordances so a glance is enough — every
 * artefact slot renders ✓ filename or ✗ "Not on file", every contact slot
 * renders the person or nothing.
 *
 * NOT impersonation. In the Case Portal, clicking a firm row enters
 * impersonation (you become the firm). That stays in the portal (ADR-0077);
 * the CC row opens this read-only card instead.
 *
 * Routing is a Handling-only concern. The routing status badge shows only when
 * the firm's designation includes Handling — meaningless for an originating-
 * only firm. The Documents section gates artefacts on the same axis:
 *   - Logo                                          → all firms
 *   - Malpractice Insurance                         → every firm (#677)
 *   - MFA — Handling                                → firm handles
 *   - W-9, MFA — Originating                        → firm originates
 *
 * Each document row is click-to-upload when empty, and exposes Replace + Delete
 * affordances when present. Delete opens an inline confirm modal (ADR-0086).
 *
 * Mirrors the Operations `CaseDetailPanel` chrome (navy backdrop, slide-in,
 * click-outside + Escape, `role=dialog`) but content has nothing in common
 * with a matter.
 */

import { useEffect, useLayoutEffect, useState } from "react";
import { Pencil, X } from "lucide-react";
import type {
  ClientSuccessFirm,
  FirmDesignation,
  FirmDocumentSlot,
  FirmExemptionRequest,
  FirmFile,
  IntakeArtifact,
} from "@command-center/shared";
import { INTAKE_ARTIFACT_LABELS } from "@command-center/shared";
import { Badge } from "@/components/ui/badge";
import { MarkNotRequiredModal } from "@/components/mark-not-required-modal";
import { PanelTaskList } from "@/components/panel-task-list";
import { Detail, Section } from "@/components/firm-panel-section";
import { DocumentRow } from "@/components/firm-panel-document-row";
import { EditableTextRow } from "@/components/firm-panel-editable-row";
import {
  ConfirmOverlay,
  DesignationConfirmOverlay,
} from "@/components/firm-panel-confirm-overlay";
import { cn } from "@/lib/utils";
import { initials, routingVariant } from "@/lib/format";
import { useInlineEdit } from "@/hooks/use-inline-edit";

const DESIGNATION_OPTIONS: FirmDesignation[] = [
  "Originating",
  "Handling",
  "Both",
];

function isFirmDesignation(value: string | null): value is FirmDesignation {
  return value === "Originating" || value === "Handling" || value === "Both";
}

/** Designation → badge variant. Handling (destination) blue; Originating
 *  (source) slate; Both emerald. */
function designationVariant(d: string | null): "blue" | "slate" | "emerald" | "muted" {
  switch (d) {
    case "Handling":
      return "blue";
    case "Originating":
      return "slate";
    case "Both":
      return "emerald";
    default:
      return "muted";
  }
}

/** Does this firm's designation include Handling? */
function firmHandles(designation: string | null): boolean {
  return designation === "Handling" || designation === "Both";
}

/** Does this firm's designation include Originating? */
function firmOriginates(designation: string | null): boolean {
  return designation === "Originating" || designation === "Both";
}

export function FirmDetailPanel({
  firm,
  onClose,
  onTogglePause,
  pausePending,
  onUploadDocument,
  onDeleteDocument,
  documentPendingSlot = null,
  documentError = null,
  onAddressUpdate,
  addressPending = false,
  addressError = null,
  onNameUpdate,
  namePending = false,
  onDesignationUpdate,
  designationPending = false,
  onNotificationsEmailUpdate,
  notificationsEmailPending = false,
  onEmailDomainsUpdate,
  emailDomainsPending = false,
  onRoutingPreferencesUpdate,
  routingPreferencesPending = false,
  fieldError = null,
  onAsk,
  onClientEmails,
  onExemption,
  exemptionPendingArtifact = null,
  testId = "firm-detail-panel",
}: {
  firm: ClientSuccessFirm;
  onClose: () => void;
  /** Toggle global routing pause for this firm. Omitted when the parent has
   *  no wiring (no-op renders as the button missing). */
  onTogglePause?: () => void;
  /** True while the pause/unpause round-trip is in flight for THIS firm. */
  pausePending?: boolean;
  /** Upload (or replace) the artefact in a slot. Omitted when the parent has
   *  no wiring — rows render read-only. */
  onUploadDocument?: (slot: FirmDocumentSlot, file: File) => void;
  /** Delete the artefact in a slot. Opens a confirm modal in the panel; the
   *  callback fires only on confirm. */
  onDeleteDocument?: (slot: FirmDocumentSlot) => void;
  /** Slot whose upload or delete is currently in flight, or null. */
  documentPendingSlot?: FirmDocumentSlot | null;
  /** User-facing error message from the last document op, or null. Cleared on
   *  the next attempt. */
  documentError?: string | null;
  /** Set or clear the Originating-side mailing address. Omitted when the
   *  parent has no wiring — row renders read-only. */
  onAddressUpdate?: (address: string | null) => void;
  /** True while the address PATCH round-trip is in flight. */
  addressPending?: boolean;
  /** User-facing error from the last address update, or null. */
  addressError?: string | null;
  /** Set the firm's display name. ADR-0091. */
  onNameUpdate?: (name: string) => void;
  namePending?: boolean;
  /** Set the firm's designation. ADR-0091. Triggers a confirm modal listing
   *  the cascade (which sections hide/show under the new value). */
  onDesignationUpdate?: (designation: FirmDesignation) => void;
  designationPending?: boolean;
  /** Set or clear the firm's Notifications Email. ADR-0091. */
  onNotificationsEmailUpdate?: (value: string | null) => void;
  notificationsEmailPending?: boolean;
  /** Set or clear the firm's Email Domains (multilineText, one per line). */
  onEmailDomainsUpdate?: (value: string | null) => void;
  emailDomainsPending?: boolean;
  /** Set or clear the firm's Routing Preferences (free-form internal notes). */
  onRoutingPreferencesUpdate?: (value: string | null) => void;
  routingPreferencesPending?: boolean;
  /** User-facing error from the last field-edit PATCH, or null. */
  fieldError?: string | null;
  /** Open the create-task modal pre-bound to this firm (Slice I / ADR-0089
   *  `+ Ask` button). Omitted when the parent has no wiring — the footer
   *  button doesn't render. */
  onAsk?: () => void;
  /** Navigate to this firm's Client-Email Review subpage (ADR-0115). Gated to
   *  OF / Both firms (the templates are the firm's client-facing emails as
   *  Originating Firm). Omitted when the parent has no wiring — the footer
   *  button doesn't render. */
  onClientEmails?: () => void;
  /** Mark / clear an intake-artifact exemption (Slice M / ADR-0100). The
   *  discriminated union enforces that `mark` carries the required free-text
   *  reason. Omitted when the parent has no wiring — the affordances don't
   *  render. */
  onExemption?: (input: FirmExemptionRequest) => void;
  /** Artifact whose exemption round-trip is in flight, or null. */
  exemptionPendingArtifact?: IntakeArtifact | null;
  testId?: string;
}) {
  // Slide-in: start off-screen, flip in after first paint (same idiom as
  // CaseDetailPanel — see its comment for why a raf is needed).
  const [mounted, setMounted] = useState(false);
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const [confirmDeleteSlot, setConfirmDeleteSlot] =
    useState<FirmDocumentSlot | null>(null);

  // Slice M (ADR-0100): which artifact the "Mark not required" reason modal
  // is open for, or null. The modal is the only path to a mark — the reason
  // is required.
  const [exemptionModalArtifact, setExemptionModalArtifact] =
    useState<IntakeArtifact | null>(null);

  // The four congruent clearable text fields (Address, Notifications Email,
  // Email Domains, Routing Preferences) share one editor shape, so each runs
  // on a useInlineEdit hook (editing flag + string draft + start/save/cancel,
  // trimming empty → null). The per-field clear-confirm overlay flag stays
  // here — it's independent of the edit buffer and mirrors the document
  // delete-confirm flow.
  const address = useInlineEdit(firm.address, onAddressUpdate, addressPending);
  const [confirmClearAddress, setConfirmClearAddress] = useState(false);
  const notificationsEmail = useInlineEdit(
    firm.notificationsEmail,
    onNotificationsEmailUpdate,
    notificationsEmailPending,
  );
  const [confirmClearNotificationsEmail, setConfirmClearNotificationsEmail] =
    useState(false);
  const emailDomains = useInlineEdit(
    firm.emailDomains,
    onEmailDomainsUpdate,
    emailDomainsPending,
  );
  const [confirmClearEmailDomains, setConfirmClearEmailDomains] =
    useState(false);
  const routingPreferences = useInlineEdit(
    firm.routingPreferences,
    onRoutingPreferencesUpdate,
    routingPreferencesPending,
  );
  const [confirmClearRoutingPreferences, setConfirmClearRoutingPreferences] =
    useState(false);

  // Name and Designation stay hand-rolled: Name rejects an empty draft (rather
  // than clearing to null) and has bespoke header chrome with no clear-confirm;
  // Designation is a two-step confirm flow (`pendingDesignation`) over a
  // non-string draft, listing the ADR-0091 cascade before the PATCH fires.
  const [nameEditing, setNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [designationEditing, setDesignationEditing] = useState(false);
  const [pendingDesignation, setPendingDesignation] =
    useState<FirmDesignation | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        // Inner overlays / editors close in priority order; only the deepest
        // open layer is cancelled per Escape press.
        if (exemptionModalArtifact) setExemptionModalArtifact(null);
        else if (confirmDeleteSlot) setConfirmDeleteSlot(null);
        else if (pendingDesignation) setPendingDesignation(null);
        else if (confirmClearAddress) setConfirmClearAddress(false);
        else if (confirmClearNotificationsEmail)
          setConfirmClearNotificationsEmail(false);
        else if (confirmClearEmailDomains) setConfirmClearEmailDomains(false);
        else if (confirmClearRoutingPreferences)
          setConfirmClearRoutingPreferences(false);
        else if (nameEditing) setNameEditing(false);
        else if (designationEditing) setDesignationEditing(false);
        else if (notificationsEmail.editing) notificationsEmail.cancel();
        else if (emailDomains.editing) emailDomains.cancel();
        else if (routingPreferences.editing) routingPreferences.cancel();
        else if (address.editing) address.cancel();
        else onClose();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [
    onClose,
    exemptionModalArtifact,
    confirmDeleteSlot,
    pendingDesignation,
    confirmClearAddress,
    confirmClearNotificationsEmail,
    confirmClearEmailDomains,
    confirmClearRoutingPreferences,
    nameEditing,
    designationEditing,
    notificationsEmail,
    emailDomains,
    routingPreferences,
    address,
  ]);

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  function requestDelete(slot: FirmDocumentSlot) {
    setConfirmDeleteSlot(slot);
  }

  function confirmDelete() {
    if (confirmDeleteSlot && onDeleteDocument) {
      onDeleteDocument(confirmDeleteSlot);
    }
    setConfirmDeleteSlot(null);
  }

  function confirmClearAddressNow() {
    if (onAddressUpdate) onAddressUpdate(null);
    setConfirmClearAddress(false);
  }

  function startNameEdit() {
    if (!onNameUpdate || namePending) return;
    setNameDraft(firm.name);
    setNameEditing(true);
  }
  function saveName() {
    if (!onNameUpdate || namePending) return;
    const trimmed = nameDraft.trim();
    if (trimmed.length === 0) return;
    onNameUpdate(trimmed);
    setNameEditing(false);
  }
  function cancelNameEdit() {
    setNameEditing(false);
    setNameDraft("");
  }

  function startDesignationEdit() {
    if (!onDesignationUpdate || designationPending) return;
    setDesignationEditing(true);
  }
  function chooseDesignation(next: FirmDesignation) {
    if (!onDesignationUpdate) return;
    if (isFirmDesignation(firm.designation) && firm.designation === next) {
      setDesignationEditing(false);
      return;
    }
    setPendingDesignation(next);
  }
  function confirmDesignation() {
    if (!onDesignationUpdate || !pendingDesignation) return;
    onDesignationUpdate(pendingDesignation);
    setPendingDesignation(null);
    setDesignationEditing(false);
  }
  function cancelDesignation() {
    setPendingDesignation(null);
  }
  function cancelDesignationEdit() {
    setDesignationEditing(false);
    setPendingDesignation(null);
  }

  function confirmClearNotificationsEmailNow() {
    if (onNotificationsEmailUpdate) onNotificationsEmailUpdate(null);
    setConfirmClearNotificationsEmail(false);
  }

  function confirmClearEmailDomainsNow() {
    if (onEmailDomainsUpdate) onEmailDomainsUpdate(null);
    setConfirmClearEmailDomains(false);
  }

  function confirmClearRoutingPreferencesNow() {
    if (onRoutingPreferencesUpdate) onRoutingPreferencesUpdate(null);
    setConfirmClearRoutingPreferences(false);
  }

  const handles = firmHandles(firm.designation);
  const originates = firmOriginates(firm.designation);
  const { primaryContact } = firm;
  // The Contact section ALWAYS renders — Primary Contact is a core at-a-glance
  // slot (its absence is itself a signal, not nothing-to-show). Notifications
  // email is a sub-row that hides when empty.
  // Documents section ALWAYS renders too — Logo applies to every firm.

  const logoFile: FirmFile | null = firm.logoUrl
    ? { name: "Logo", url: firm.logoUrl }
    : null;

  const labelForSlot: Record<FirmDocumentSlot, string> = {
    logo: "Logo",
    w9: "W-9",
    malpractice: "Malpractice insurance",
    mfaH: "MFA — Handling",
    mfaO: "MFA — Originating",
  };

  // Slice M (ADR-0100): per-artifact exemption affordance wiring. The five
  // document-slot keys ARE intake-artifact keys; primaryContact is handled
  // inline in the Contact section.
  const exemptSet = new Set(firm.exemptArtifacts);
  const exemptionPropsFor = (artifact: IntakeArtifact) => ({
    exempt: exemptSet.has(artifact),
    exemptionPending: exemptionPendingArtifact === artifact,
    onMarkNotRequired: onExemption
      ? () => setExemptionModalArtifact(artifact)
      : undefined,
    onRestoreRequired: onExemption
      ? () => onExemption({ action: "clear", artifact })
      : undefined,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex"
      role="presentation"
      onMouseDown={onBackdropClick}
      data-testid={`${testId}-overlay`}
    >
      <div
        aria-hidden="true"
        className={`absolute inset-0 bg-[#000527] transition-opacity duration-200 ${
          mounted ? "opacity-40" : "opacity-0"
        }`}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Firm detail for ${firm.name}`}
        className={`relative ml-auto flex h-full w-full max-w-[580px] flex-col border-l bg-card text-card-foreground shadow-xl transition-transform duration-200 ${
          mounted ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid={testId}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between border-b px-5 py-4">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {firm.logoUrl ? (
              <img
                src={firm.logoUrl}
                alt=""
                className="h-10 w-10 shrink-0 rounded-md border bg-white object-contain"
                data-testid={`${testId}-logo`}
              />
            ) : (
              <div
                aria-hidden="true"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted text-xs font-semibold uppercase text-muted-foreground"
                data-testid={`${testId}-logo-fallback`}
              >
                {initials(firm.name)}
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              {/* Firm Name — inline editor (ADR-0091). Hover surfaces a pencil;
                  editing flips the span into a text input with Save/Cancel. */}
              {nameEditing ? (
                <div
                  className="flex items-center gap-2"
                  data-testid={`${testId}-name`}
                  data-state="editing"
                >
                  <input
                    type="text"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    disabled={namePending}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveName();
                    }}
                    className="min-w-0 flex-1 rounded-sm border bg-background px-2 py-1 text-base font-semibold text-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60"
                    data-testid={`${testId}-name-input`}
                  />
                  <button
                    type="button"
                    onClick={cancelNameEdit}
                    disabled={namePending}
                    className="rounded-md border px-2 py-1 text-xs font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
                    data-testid={`${testId}-name-cancel`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveName}
                    disabled={namePending || nameDraft.trim().length === 0}
                    className="rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                    data-testid={`${testId}-name-save`}
                  >
                    {namePending ? "Saving…" : "Save"}
                  </button>
                </div>
              ) : (
                <div
                  className="group flex items-center gap-2"
                  data-testid={`${testId}-name`}
                  data-state="display"
                >
                  <span className="truncate text-base font-semibold">
                    {firm.name}
                  </span>
                  {onNameUpdate ? (
                    <button
                      type="button"
                      onClick={startNameEdit}
                      disabled={namePending}
                      className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground focus-within:opacity-100 group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Edit firm name"
                      data-testid={`${testId}-name-edit`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              )}

              {/* Designation badge — inline dropdown editor (ADR-0091).
                  Editing surfaces a tiny dropdown; selecting a non-current
                  value opens a confirm modal listing what hides/shows. */}
              <div className="flex flex-wrap items-center gap-2">
                {designationEditing ? (
                  <div
                    className="flex items-center gap-2"
                    data-testid={`${testId}-designation`}
                    data-state="editing"
                  >
                    <select
                      value={
                        isFirmDesignation(firm.designation)
                          ? firm.designation
                          : DESIGNATION_OPTIONS[0]
                      }
                      onChange={(e) =>
                        chooseDesignation(e.target.value as FirmDesignation)
                      }
                      disabled={designationPending}
                      autoFocus
                      className="rounded-sm border bg-background px-2 py-1 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60"
                      data-testid={`${testId}-designation-select`}
                    >
                      {DESIGNATION_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={cancelDesignationEdit}
                      disabled={designationPending}
                      className="rounded-md border px-2 py-1 text-xs font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
                      data-testid={`${testId}-designation-cancel`}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div
                    className="group flex items-center gap-1"
                    data-testid={`${testId}-designation`}
                    data-state="display"
                  >
                    {firm.designation ? (
                      <Badge variant={designationVariant(firm.designation)}>
                        {firm.designation}
                      </Badge>
                    ) : (
                      <span className="italic text-muted-foreground">
                        No designation
                      </span>
                    )}
                    {onDesignationUpdate ? (
                      <button
                        type="button"
                        onClick={startDesignationEdit}
                        disabled={designationPending}
                        className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground focus-within:opacity-100 group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Edit designation"
                        data-testid={`${testId}-designation-edit`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                )}
                {handles && firm.routingStatus ? (
                  <Badge variant={routingVariant(firm.routingStatus)}>
                    {firm.routingStatus}
                  </Badge>
                ) : null}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close firm detail"
            data-testid={`${testId}-close`}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {fieldError ? (
            <div
              className="border-b border-destructive/40 bg-destructive/5 px-5 py-2 text-xs text-destructive"
              data-testid={`${testId}-field-error`}
            >
              {fieldError}
            </div>
          ) : null}
          {/* Slice M (ADR-0100): designation-drift banner. Shows while a
              designation flip has made exempt artifacts applicable and each
              one is still unreviewed — Restore as required OR re-affirm via
              Mark not required resolves its entry. */}
          {firm.designationAlert !== null &&
          firm.designationAlert.conflictingExemptions.length > 0 ? (
            <div
              className="border-b border-warning-border bg-warning-bg px-5 py-2 text-xs text-warning"
              data-testid={`${testId}-designation-alert`}
            >
              ⚠ {firm.name} is now {firm.designation ?? "—"}
              {firm.designationAlert.prevDesignation
                ? ` (was ${firm.designationAlert.prevDesignation})`
                : ""}{" "}
              — review exemptions:{" "}
              {firm.designationAlert.conflictingExemptions
                .map((a) => `${INTAKE_ARTIFACT_LABELS[a]} (marked not required)`)
                .join(", ")}
            </div>
          ) : null}
          <Section title="Contact" testId={`${testId}-contact`}>
            <dl className="flex flex-col gap-2">
              <EditableTextRow
                label="Notifications"
                value={firm.notificationsEmail}
                placeholder="firm@example.com"
                draft={notificationsEmail.draft}
                setDraft={notificationsEmail.setDraft}
                editing={notificationsEmail.editing}
                pending={notificationsEmailPending}
                multiline={false}
                onStart={
                  onNotificationsEmailUpdate ? notificationsEmail.start : undefined
                }
                onSave={notificationsEmail.save}
                onCancel={notificationsEmail.cancel}
                onRequestClear={() => setConfirmClearNotificationsEmail(true)}
                testId={`${testId}-notifications-email`}
              />
              <EditableTextRow
                label="Email domains"
                value={firm.emailDomains}
                placeholder={"example.com\nanother-firm.com"}
                draft={emailDomains.draft}
                setDraft={emailDomains.setDraft}
                editing={emailDomains.editing}
                pending={emailDomainsPending}
                multiline={true}
                rows={3}
                maxLength={2000}
                onStart={onEmailDomainsUpdate ? emailDomains.start : undefined}
                onSave={emailDomains.save}
                onCancel={emailDomains.cancel}
                onRequestClear={() => setConfirmClearEmailDomains(true)}
                testId={`${testId}-email-domains`}
              />
              {primaryContact ? (
                  <Detail
                    label="Primary contact"
                    value={
                      <div
                        className="flex flex-col gap-0.5"
                        data-testid={`${testId}-primary-contact`}
                      >
                        <span className="font-medium">
                          {primaryContact.name}
                        </span>
                        {primaryContact.email ? (
                          <span className="text-muted-foreground">
                            {primaryContact.email}
                          </span>
                        ) : null}
                        {primaryContact.phone ? (
                          <span className="text-muted-foreground">
                            {primaryContact.phone}
                          </span>
                        ) : null}
                        {firm.primaryContactAmbiguous ? (
                          <span
                            className="mt-1 text-xs italic text-warning"
                            data-testid={`${testId}-primary-contact-ambiguous`}
                          >
                            More than one primary contact linked — showing the
                            first. Resolve in Airtable.
                          </span>
                        ) : null}
                      </div>
                    }
                  />
                ) : (
                  <Detail
                    label="Primary contact"
                    value={
                      exemptSet.has("primaryContact") ? (
                        <span className="flex items-center gap-2">
                          <span
                            className="italic text-muted-foreground"
                            data-testid={`${testId}-primary-contact-not-required`}
                          >
                            Not required
                          </span>
                          {onExemption ? (
                            <button
                              type="button"
                              onClick={() =>
                                onExemption({
                                  action: "clear",
                                  artifact: "primaryContact",
                                })
                              }
                              disabled={
                                exemptionPendingArtifact === "primaryContact"
                              }
                              className="text-xs underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground disabled:cursor-not-allowed disabled:opacity-50"
                              data-testid={`${testId}-primary-contact-restore-required`}
                            >
                              Restore as required
                            </button>
                          ) : null}
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <span
                            className="italic text-muted-foreground"
                            data-testid={`${testId}-primary-contact-missing`}
                          >
                            Not on file
                          </span>
                          {onExemption ? (
                            <button
                              type="button"
                              onClick={() =>
                                setExemptionModalArtifact("primaryContact")
                              }
                              disabled={
                                exemptionPendingArtifact === "primaryContact"
                              }
                              className="text-xs text-muted-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:text-foreground hover:decoration-foreground disabled:cursor-not-allowed disabled:opacity-50"
                              data-testid={`${testId}-primary-contact-mark-not-required`}
                            >
                              Mark not required
                            </button>
                          ) : null}
                        </span>
                      )
                    }
                  />
                )}
            </dl>
          </Section>

          <Section title="Documents" testId={`${testId}-documents`}>
            {documentError ? (
              <p
                className="mb-2 rounded-sm border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive"
                data-testid={`${testId}-document-error`}
              >
                {documentError}
              </p>
            ) : null}
            <ul className="flex flex-col gap-0.5">
              <DocumentRow
                label="Logo"
                slot="logo"
                file={logoFile}
                testId={`${testId}-doc-logo`}
                pending={documentPendingSlot === "logo"}
                {...exemptionPropsFor("logo")}
                onUpload={onUploadDocument}
                onDelete={onUploadDocument ? requestDelete : undefined}
              />
              <DocumentRow
                label="Malpractice insurance"
                slot="malpractice"
                file={firm.documents.malpracticeInsurance}
                testId={`${testId}-doc-malpractice`}
                pending={documentPendingSlot === "malpractice"}
                {...exemptionPropsFor("malpractice")}
                onUpload={onUploadDocument}
                onDelete={onUploadDocument ? requestDelete : undefined}
              />
              {handles ? (
                <DocumentRow
                  label="MFA — Handling"
                  slot="mfaH"
                  file={firm.documents.mfaHandling}
                  testId={`${testId}-doc-mfa-handling`}
                  pending={documentPendingSlot === "mfaH"}
                  {...exemptionPropsFor("mfaH")}
                  onUpload={onUploadDocument}
                  onDelete={onUploadDocument ? requestDelete : undefined}
                />
              ) : null}
              {originates ? (
                <>
                  <DocumentRow
                    label="W-9"
                    slot="w9"
                    file={firm.documents.w9}
                    testId={`${testId}-doc-w9`}
                    pending={documentPendingSlot === "w9"}
                {...exemptionPropsFor("w9")}
                    onUpload={onUploadDocument}
                    onDelete={onUploadDocument ? requestDelete : undefined}
                  />
                  <EditableTextRow
                    label="Address"
                    value={firm.address}
                    placeholder={"123 Main St\nSpringfield IL 62701"}
                    draft={address.draft}
                    setDraft={address.setDraft}
                    editing={address.editing}
                    pending={addressPending}
                    multiline={true}
                    rows={4}
                    maxLength={500}
                    onStart={onAddressUpdate ? address.start : undefined}
                    onSave={address.save}
                    onCancel={address.cancel}
                    onRequestClear={() => setConfirmClearAddress(true)}
                    testId={`${testId}-address`}
                  />
                  {addressError ? (
                    <li
                      className="rounded-sm border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive"
                      data-testid={`${testId}-address-error`}
                    >
                      {addressError}
                    </li>
                  ) : null}
                  <DocumentRow
                    label="MFA — Originating"
                    slot="mfaO"
                    file={firm.documents.mfaOriginating}
                    testId={`${testId}-doc-mfa-originating`}
                    pending={documentPendingSlot === "mfaO"}
                {...exemptionPropsFor("mfaO")}
                    onUpload={onUploadDocument}
                    onDelete={onUploadDocument ? requestDelete : undefined}
                  />
                </>
              ) : null}
            </ul>
          </Section>

          <Section title="Notes" testId={`${testId}-notes`}>
            <dl className="flex flex-col gap-2">
              <EditableTextRow
                label="Routing preferences"
                value={firm.routingPreferences}
                placeholder="Internal notes about how leads should be routed to this firm."
                draft={routingPreferences.draft}
                setDraft={routingPreferences.setDraft}
                editing={routingPreferences.editing}
                pending={routingPreferencesPending}
                multiline={true}
                rows={4}
                maxLength={4000}
                onStart={
                  onRoutingPreferencesUpdate
                    ? routingPreferences.start
                    : undefined
                }
                onSave={routingPreferences.save}
                onCancel={routingPreferences.cancel}
                onRequestClear={() => setConfirmClearRoutingPreferences(true)}
                testId={`${testId}-routing-preferences`}
              />
            </dl>
          </Section>

          <Section title="Cases">
            <dl className="flex flex-col gap-1 text-sm">
              <Detail
                label="As handling firm"
                value={
                  <span data-testid={`${testId}-placement-count`}>
                    {firm.placementCount} placement
                    {firm.placementCount === 1 ? "" : "s"}
                  </span>
                }
              />
              <Detail
                label="As originating firm"
                value={
                  <span data-testid={`${testId}-lead-count`}>
                    {firm.leadCount} lead{firm.leadCount === 1 ? "" : "s"}
                  </span>
                }
              />
            </dl>
            <p className="mt-1 text-xs italic text-muted-foreground">
              A page listing these matters is a later phase.
            </p>
          </Section>

          <Section title="People">
            <p className="text-sm" data-testid={`${testId}-contact-count`}>
              {firm.contactCount} user{firm.contactCount === 1 ? "" : "s"}
            </p>
          </Section>

          <Section title="Tasks" testId={`${testId}-tasks`}>
            <PanelTaskList
              openTasks={firm.tasks}
              closedTasks={firm.recentlyClosedTasks}
              testId={`${testId}-tasks`}
              emptyLabel="No tasks about this firm."
            />
          </Section>
        </div>

        {/* Action footer — the footer Edit button was deleted in ADR-0091
            (every editable field has its own pencil affordance now, so the
            global Edit has nothing to do). Pause/Unpause writes
            Firms.Routing Status globally. */}
        <footer
          className="flex items-center gap-2 border-t px-5 py-3"
          data-testid={`${testId}-actions`}
        >
          {/* ADR-0091: the inert footer Edit button is gone — every editable
              field has its own pencil affordance in the panel now. */}
          {onAsk ? (
            <button
              type="button"
              onClick={onAsk}
              data-testid={`${testId}-ask`}
              className="rounded-md border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              + Ask
            </button>
          ) : null}
          {handles && onTogglePause ? (
            <button
              type="button"
              onClick={onTogglePause}
              disabled={pausePending}
              data-testid={`${testId}-pause`}
              className={cn(
                "rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                pausePending
                  ? "cursor-not-allowed text-muted-foreground opacity-60"
                  : "text-foreground hover:bg-accent",
              )}
            >
              {pausePending
                ? firm.routingStatus === "Paused"
                  ? "Unpausing…"
                  : "Pausing…"
                : firm.routingStatus === "Paused"
                  ? "Unpause"
                  : "Pause"}
            </button>
          ) : null}
          {originates && onClientEmails ? (
            <button
              type="button"
              onClick={onClientEmails}
              data-testid={`${testId}-client-emails`}
              className="rounded-md border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              Client emails
            </button>
          ) : null}
        </footer>

        {confirmDeleteSlot ? (
          <ConfirmOverlay
            title={`Delete ${labelForSlot[confirmDeleteSlot]}?`}
            description="This removes the file from Airtable. You can upload a new one any time."
            confirmLabel="Delete"
            onCancel={() => setConfirmDeleteSlot(null)}
            onConfirm={confirmDelete}
            testId={`${testId}-confirm-delete`}
          />
        ) : null}

        {exemptionModalArtifact !== null && onExemption ? (
          <MarkNotRequiredModal
            firmName={firm.name}
            artifactLabel={INTAKE_ARTIFACT_LABELS[exemptionModalArtifact]}
            pending={exemptionPendingArtifact === exemptionModalArtifact}
            onSubmit={(reason) => {
              onExemption({
                action: "mark",
                artifact: exemptionModalArtifact,
                reason,
              });
              setExemptionModalArtifact(null);
            }}
            onClose={() => setExemptionModalArtifact(null)}
            testId={`${testId}-mark-not-required`}
          />
        ) : null}

        {confirmClearAddress ? (
          <ConfirmOverlay
            title="Clear address?"
            description="This removes the mailing address from Airtable. You can re-enter it any time."
            confirmLabel="Clear"
            onCancel={() => setConfirmClearAddress(false)}
            onConfirm={confirmClearAddressNow}
            testId={`${testId}-confirm-clear-address`}
          />
        ) : null}

        {pendingDesignation ? (
          <DesignationConfirmOverlay
            current={
              isFirmDesignation(firm.designation) ? firm.designation : null
            }
            next={pendingDesignation}
            pending={designationPending}
            onCancel={cancelDesignation}
            onConfirm={confirmDesignation}
            testId={`${testId}-confirm-designation`}
          />
        ) : null}

        {confirmClearNotificationsEmail ? (
          <ConfirmOverlay
            title="Clear notifications email?"
            description="This removes the email from Airtable. Client comms will skip the notification BCC until set again."
            confirmLabel="Clear"
            onCancel={() => setConfirmClearNotificationsEmail(false)}
            onConfirm={confirmClearNotificationsEmailNow}
            testId={`${testId}-confirm-clear-notifications-email`}
          />
        ) : null}

        {confirmClearEmailDomains ? (
          <ConfirmOverlay
            title="Clear email domains?"
            description="This removes the email domain list from Airtable. The Lead Parser will fall back to AI name match for inbound from these domains."
            confirmLabel="Clear"
            onCancel={() => setConfirmClearEmailDomains(false)}
            onConfirm={confirmClearEmailDomainsNow}
            testId={`${testId}-confirm-clear-email-domains`}
          />
        ) : null}

        {confirmClearRoutingPreferences ? (
          <ConfirmOverlay
            title="Clear routing preferences?"
            description="This removes the routing-preference notes from Airtable. You can re-enter them any time."
            confirmLabel="Clear"
            onCancel={() => setConfirmClearRoutingPreferences(false)}
            onConfirm={confirmClearRoutingPreferencesNow}
            testId={`${testId}-confirm-clear-routing-preferences`}
          />
        ) : null}
      </aside>
    </div>
  );
}
